import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, Star, ArrowRight, Download, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ExtensionSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    // Verify the payment was successful
    if (sessionId) {
      verifyPayment();
    } else {
      setLoading(false);
      setVerified(true); // Assume success if redirected here
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      // In production, verify the session with backend
      // For now, we trust the redirect
      setVerified(true);
    } catch (error) {
      console.error('Verification error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3EF1C7]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1120] text-white">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/5 via-transparent to-[#3EF1C7]/5 pointer-events-none" />
      
      <div className="relative max-w-2xl mx-auto px-6 py-20">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFA500] mb-6">
            <CheckCircle className="w-12 h-12 text-[#0B1120]" />
          </div>
          
          <h1 className="text-4xl font-bold mb-4">
            Welcome to <span className="text-[#FFD700]">Premium!</span>
          </h1>
          
          <p className="text-xl text-slate-400">
            Your subscription is now active. Enjoy all premium features!
          </p>
        </div>

        {/* Premium Badge */}
        <div className="bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/10 border border-[#FFD700]/30 rounded-2xl p-8 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 rounded-xl bg-[#FFD700]/20">
              <Star className="w-8 h-8 text-[#FFD700]" fill="#FFD700" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Commerce Index Premium</h2>
              <p className="text-[#FFD700]">$4.99/month • All features unlocked</p>
            </div>
          </div>

          {/* Features unlocked */}
          <div className="grid grid-cols-2 gap-4">
            {[
              'Unlimited price comparisons',
              'Price history & trends',
              'Price drop alerts',
              'Priority support',
              'Ad-free experience',
              'Data export'
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FFD700]" />
                <span className="text-sm text-slate-300">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Receipt Info */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[#3EF1C7]" />
            Payment Confirmed
          </h3>
          <p className="text-slate-400 text-sm mb-4">
            A receipt has been sent to your email address. You can manage your subscription 
            anytime from the extension settings or your Stripe customer portal.
          </p>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>Session ID:</span>
            <code className="bg-slate-900 px-2 py-1 rounded text-xs">
              {sessionId ? sessionId.substring(0, 20) + '...' : 'N/A'}
            </code>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold mb-4">Next Steps</h3>
          <ol className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#3EF1C7]/20 text-[#3EF1C7] flex items-center justify-center text-sm font-bold">1</span>
              <div>
                <p className="font-medium">Refresh your extension</p>
                <p className="text-sm text-slate-400">Click the Commerce Index icon in your browser toolbar</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#3EF1C7]/20 text-[#3EF1C7] flex items-center justify-center text-sm font-bold">2</span>
              <div>
                <p className="font-medium">See your Premium badge</p>
                <p className="text-sm text-slate-400">You'll see a gold "Premium" badge confirming your status</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#3EF1C7]/20 text-[#3EF1C7] flex items-center justify-center text-sm font-bold">3</span>
              <div>
                <p className="font-medium">Start saving money!</p>
                <p className="text-sm text-slate-400">Browse any product page to see price comparisons</p>
              </div>
            </li>
          </ol>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={() => window.close()}
            className="flex-1 bg-[#3EF1C7] hover:bg-[#3EF1C7]/90 text-[#0B1120] font-semibold"
          >
            Close & Start Shopping
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            onClick={() => window.open('https://commerceindex.com/deals', '_blank')}
            variant="outline"
            className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            Browse Deals
          </Button>
        </div>

        {/* Support link */}
        <p className="text-center text-sm text-slate-500 mt-8">
          Need help? <a href="mailto:support@commerceindex.com" className="text-[#3EF1C7] hover:underline">Contact Support</a>
        </p>
      </div>
    </div>
  );
}
